import {OperableAccount, OperableBank, OperableTarget, OperationModel} from '~/redux/finances/domain';
import {groupOperationsByType} from '~/redux/finances/financesPrimitives';
import {FinancialData} from '~/redux/finances/financesReducer';
import {IntersectionOf} from '~/shared/utils/types';

export type Select<T> = (data: FinancialData) => T | null | undefined;

export const hasBankOfType = (() => {
    type BankType = OperableBank['method'];
    type OperationWith<TBank extends BankType> = OperationModel & {target: OperableBank & {bankType: TBank}};

    return <T extends BankType>(type: T) =>
        (op: OperationModel): op is OperationWith<T> =>
            op.target.type === 'payment_network' && op.target.method === type;
})();

export const hasAccountOfType = (() => {
    type OperationWith<TAccount extends OperableAccount.Type> = OperationModel & {
        account: OperableAccount & {type: TAccount};
    };
    return <T extends OperableAccount.Type>(type: T) =>
        (op: OperationModel): op is OperationWith<T> =>
            op.account.type === type;
})();

export type OperationWithTargetOf<T extends OperableTarget.Type> = OperationModel & {
    target: OperableTarget & {type: T};
};

export const hasTargetOfType = (() => {
    return <T extends OperableTarget.Type>(type: T) =>
        (op: OperationModel): op is OperationWithTargetOf<T> =>
            op.target.type === type;
})();

export const {isEveryOf, isSomeOf} = (() => {
    type TypeGuard<T> = (thing: any) => thing is T;
    type TypeFromGuard<M extends TypeGuard<any>> = M extends TypeGuard<infer T> ? T : never;

    const isSomeOf =
        <Guards extends [...TypeGuard<any>[]]>(...matchers: Guards) =>
        (thing: unknown): thing is TypeFromGuard<Guards[number]> =>
            matchers.some(match => match(thing));

    const isEveryOf =
        <Guards extends [...TypeGuard<any>[]]>(...matchers: Guards) =>
        (thing: unknown): thing is IntersectionOf<TypeFromGuard<Guards[number]>> =>
            matchers.every(match => match(thing));

    return {isEveryOf, isSomeOf};
})();

export const uniqueBy =
    <T>(getHashOrKey: (item: T) => string | number | symbol) =>
    <U extends T>(array: U[]): U[] => {
        const map = new Map<string | number | symbol, U>();
        for (const item of array) map.set(getHashOrKey(item), item);
        return Array.from(map.values());
    };

export const uniqueById = uniqueBy<{id: string | number}>(({id}) => id);

export const filter = (() => {
    type falsy = undefined | null | false | '' | 0 | 0n;
    type Predicate<T extends {}, S extends T> = (item: T, idx: number, arr: T[]) => S | falsy;

    return <T extends {}, S extends T>(array: T[], predicate: Predicate<T, S>): S[] => {
        const [...results] = (function* () {
            for (let i = 0; i < array.length; i++) {
                const item = predicate(array[i], i, array);
                if (item) yield item;
            }
        })();
        return results;
    };
})();

export const selectTopupFiatOperations = (data: FinancialData) => {
    return groupOperationsByType(data)
        .topup.filter(op => op.account.type === 'fiat')
        .filter(isSomeOf(hasTargetOfType('card'), hasTargetOfType('payment_network')));
};

export const selectWithdrawFiatOperations = (data: FinancialData) => {
    return groupOperationsByType(data)
        .withdraw.filter(op => op.account.type === 'fiat')
        .filter(hasTargetOfType('payment_network'));
};

export const isNotEmpty = <T>(array: T[]): array is [T, ...T[]] => 0 in array;
